import { useEffect, useState } from "react";
import {
  useAlertStore,
  usePopupStore,
  useThemeStore,
  useUserStore,
} from "../../../stores";
import { Column, Row, ScrollRow } from "../../containers";
import { AlignItems, JustifyContent } from "../../containers/types";
import { ChequeDetailsProps } from "./types";
import { PopupBack, PopupClose, QuestionBox } from "../../../popup";
import { Heading } from "../../typography";
import {
  DateInput,
  FileInput,
  NumberInput,
  SelectorInput,
  TextInput,
} from "../../inputs";
import { ChequeApi, FactorApi, HOST_CHEQUE_FILES } from "../../../api";
import {
  Bank,
  Cheque,
  ChequeStatusType,
  ChequeUpdate,
  Vec2,
} from "../../../types";
import { BANKS, CHEQUE_STATUS_LIST } from "../../../constants";
import { IconAnimation } from "../../animation";
import {
  BackIcon,
  CheckIcon,
  EditIcon,
  LogoutIcon,
  TrashIcon,
} from "../../../assets/icons";
import ChequeStatusUpdater from "../cheque-status-updater";
import ActivityRenderer from "../../activity";
import { ActivityRendererType } from "../../activity/types";
import OptionList from "../../optionlist";
import { OptionListOption } from "../../optionlist/types";
import { factorTypeToString } from "../../../utils";
import { FactorDetails } from "../../factor";

const ChequeDetails = <T extends {}>(props: ChequeDetailsProps<T>) => {
  const [editMode, setEditMode] = useState<boolean>(false);
  const [u_cheque, setUCheque] = useState<ChequeUpdate>({
    account_no: "",
    bank: null,
    confirmed_with: "",
    date: props.date,
    image: null,
    name: "",
    number: "",
    price: props.price,
    shenase: "",
  });

  const setUChequeValue = <T extends keyof ChequeUpdate>(
    key: T,
    value: ChequeUpdate[T]
  ): void => {
    setUCheque((prev) => ({ ...prev, [key]: value }));
  };

  const [cheque, setCheque] = useState<Cheque>({ ...props });

  const setPopupProperties = usePopupStore((s) => s.setPopupProperties);
  const setPopupBody = usePopupStore((s) => s.setPopupBody);
  const theme = useThemeStore((s) => s.theme);
  const pushAlert = useAlertStore((s) => s.pushAlert);
  const hasUserAccess = useUserStore((s) => s.hasUserAccess);

  useEffect(() => {
    setPopupProperties({
      style: {
        maxHeight: "80%",
        overflowY: "auto",
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const onSave = (): void => {
    ChequeApi.update(u_cheque, props._id.$oid)
      .then((res) => {
        setCheque(res);
        pushAlert("عملیات موفقیت آمیز");
      })
      .catch((err) => {
        pushAlert(err.msg || "unknown error", "error");
      })
      .finally(() => {
        if (props.updateCb) props.updateCb();
        setEditMode(false);
      });
  };

  const onDelete = (): void => {
    if (!hasUserAccess(["EditFactorCheque"]))
      return pushAlert("دسترسی غیر مجاز", "error");

    setPopupBody(
      <QuestionBox<ChequeDetailsProps<T>>
        close={false}
        question={"آیا مطمئن هستید که می خواهید این چک را حذف کنید؟"}
        back={{ component: ChequeDetails, props: { ...props, ...cheque } }}
        onNo={() => {
          setPopupBody(<ChequeDetails {...props} />);
        }}
        onYes={() => {
          ChequeApi.deleteCheque(props._id.$oid)
            .then(() => {
              if (props.updateCb) props.updateCb();

              if (props.back) {
                setPopupBody(<props.back.component {...props.back.props} />);
              } else {
                setPopupBody(null);
              }

              pushAlert("عملیات موفقیت آمیز");
            })
            .catch((err) => pushAlert(err.msg || "unknown error", "error"));
        }}
      />
    );
  };

  const [mousePos, setMousePos] = useState<Vec2>({ x: 0, y: 0 });
  const [factorsOptions, setFactorsOptions] = useState<OptionListOption[]>([]);

  const onRightClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ): void => {
    e.preventDefault();
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  useEffect(() => {
    let res: OptionListOption[] = cheque.factors.map((f) => ({
      title: `برو به فاکتور ${factorTypeToString(f.type)}`,
      Icon: LogoutIcon,
      onClick: () => {
        FactorApi.getById(f._id.$oid)
          .then((res) => {
            setPopupBody(
              <FactorDetails<ChequeDetailsProps<T>>
                factor={res}
                back={{ component: ChequeDetails, props }}
                updateCb={props.updateCb}
              />
            );
          })
          .catch((err) => {
            pushAlert(err.msg || "unknown error", "error");
          });
      },
    }));

    setFactorsOptions(res);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cheque]);

  return (
    <Column
      alignItems={AlignItems.Center}
      justifyContent={JustifyContent.Start}
      style={{ marginBottom: "1rem", position: "relative" }}
      gap={20}
      props={{ onContextMenu: onRightClick }}
    >
      <PopupClose />
      {props.back && !editMode ? (
        <PopupBack Component={props.back.component} props={props.back.props} />
      ) : (
        <></>
      )}

      <OptionList
        mousePos={mousePos}
        options={[
          {
            title: "ویرایش",
            onClick: () => {
              if (!hasUserAccess(["EditFactorCheque"]))
                return pushAlert("دسترسی غیر مجاز", "error");

              setEditMode(true);
            },
            Icon: EditIcon,
          },
          {
            title: "حذف چک",
            onClick: onDelete,
            Icon: TrashIcon,
            color: theme.alert,
            iconColor: theme.alert,
          },
          ...factorsOptions,
        ]}
      />

      {editMode ? (
        <IconAnimation
          Icon={CheckIcon}
          onHoverColor={theme.primary}
          style={{
            position: "absolute",
            top: 5,
            left: 45,
            cursor: "pointer",
          }}
          width={24}
          height={24}
          onClick={onSave}
        />
      ) : (
        <></>
      )}
      {editMode ? (
        <IconAnimation
          Icon={BackIcon}
          onHoverColor={theme.alert}
          style={{ position: "absolute", top: 5, left: 10, cursor: "pointer" }}
          width={24}
          height={24}
          onClick={() => setEditMode(false)}
        />
      ) : (
        <></>
      )}

      <Heading
        size={18}
        textStyles={{ marginTop: "1rem" }}
        color={theme.textOnOverlay}
      >
        {!editMode ? "اطلاعات چک" : "ویرایش چک"}
      </Heading>

      <Row style={{ width: "90%" }} gap={20} className="factor-activity-row">
        <TextInput
          label="نام طرف حساب"
          value={!editMode ? cheque.name : u_cheque.name}
          setValue={(v) => setUChequeValue("name", v)}
          inputProps={{ readOnly: !editMode, placeholder: cheque.name }}
        />

        <TextInput
          label="شماره چک"
          value={!editMode ? cheque.number : u_cheque.number}
          setValue={(v) => setUChequeValue("number", v)}
          inputProps={{ readOnly: !editMode, placeholder: cheque.number }}
        />

        <TextInput
          label="شناسه"
          value={!editMode ? cheque.shenase : u_cheque.shenase}
          setValue={(v) => setUChequeValue("shenase", v)}
          inputProps={{ readOnly: !editMode, placeholder: cheque.shenase }}
        />
      </Row>

      <Row style={{ width: "90%" }} gap={20} className="factor-activity-row">
        <DateInput
          date={!editMode ? cheque.date : u_cheque.date}
          setDate={(v) => setUChequeValue("date", v as number)}
          label="تاریخ"
          readOnly={!editMode}
        />

        <SelectorInput<Bank>
          items={BANKS}
          label="بانک"
          selected={
            !editMode
              ? {
                  name: cheque.bank,
                  fa: BANKS.filter((b) => b.name === cheque.bank)[0].fa, // dangerous
                }
              : u_cheque.bank
          }
          setSelected={(v) => setUChequeValue("bank", v as Bank | null)}
          compareFn={(b, v) => b.fa.toLowerCase().includes(v.toLowerCase())}
          stringField={"fa"}
          uniqueKey={"name"}
          renderStrings={["fa"]}
          readOnly={!editMode}
        />

        <TextInput
          label="شماره حساب"
          value={!editMode ? cheque.account_no : u_cheque.account_no}
          setValue={(v) => setUChequeValue("account_no", v)}
          inputProps={{ readOnly: !editMode, placeholder: cheque.account_no }}
        />
      </Row>

      <Row style={{ width: "90%" }} gap={20} className="factor-activity-row">
        <NumberInput
          label="مبلغ (ریال)"
          value={!editMode ? cheque.price : u_cheque.price}
          setValue={(v) => setUChequeValue("price", v as number)}
          price
          inputProps={{ readOnly: !editMode }}
        />

        {!editMode ? (
          <SelectorInput<ChequeStatusType>
            items={CHEQUE_STATUS_LIST}
            label="وضعیت چک"
            selected={{
              status: cheque.status,
              fa: CHEQUE_STATUS_LIST.filter(
                (s) => s.status === cheque.status
              )[0].fa, // dangerous
            }}
            setSelected={() => {}}
            compareFn={(s, v) => s.fa.toLowerCase().includes(v.toLowerCase())}
            stringField={"fa"}
            uniqueKey={"status"}
            renderStrings={["fa"]}
            readOnly={!editMode}
          />
        ) : (
          <></>
        )}

        {editMode ? (
          <FileInput
            label="عکس چک"
            value={u_cheque.image}
            setValue={(v) => setUChequeValue("image", v)}
          />
        ) : (
          <></>
        )}

        {cheque.confirmed_with || (editMode && cheque.status !== "received") ? (
          <TextInput
            label="تایید شده به نام"
            value={
              !editMode ? cheque.confirmed_with || "" : u_cheque.confirmed_with
            }
            setValue={(v) => setUChequeValue("confirmed_with", v)}
            inputProps={{
              readOnly: !editMode,
              placeholder: cheque.confirmed_with,
            }}
          />
        ) : (
          <></>
        )}
      </Row>

      {!editMode ? (
        <Row
          style={{ width: "90%" }}
          gap={20}
          className="factor-activity-row"
          justifyContent={JustifyContent.End}
        >
          <ChequeStatusUpdater<ChequeDetailsProps<T>>
            cheque={{ ...props }}
            status={cheque.status}
            back={{ component: ChequeDetails, props: { ...props, ...cheque } }}
            cb={(c) => {
              if (props.updateCb) props.updateCb();
              if (c) setPopupBody(<ChequeDetails {...props} {...c} />);
              else setPopupBody(<ChequeDetails {...props} {...cheque} />);
            }}
          />
        </Row>
      ) : (
        <></>
      )}

      {!editMode ? (
        <>
          <Heading
            size={18}
            textStyles={{ width: "90%", textAlign: "right" }}
            color={theme.textOnOverlay}
          >
            رویداد های این چک
          </Heading>
          <Row style={{ width: "90%" }}>
            <ScrollRow gap={20} height={300}>
              {cheque.activities.map((activity, idx) => (
                <ActivityRenderer<ChequeDetailsProps<T>>
                  key={idx}
                  activity={activity}
                  type={ActivityRendererType.Small}
                  back={{
                    component: ChequeDetails,
                    props: { ...props, ...cheque },
                  }}
                />
              ))}
            </ScrollRow>
          </Row>
        </>
      ) : (
        <></>
      )}

      {!editMode ? (
        <>
          <Heading
            size={18}
            textStyles={{ width: "90%", textAlign: "right" }}
            color={theme.textOnOverlay}
          >
            عکس چک
          </Heading>
          <Row
            style={{ width: "90%" }}
            gap={20}
            className="factor-activity-row"
          >
            <img
              alt="cheque-img"
              src={`${HOST_CHEQUE_FILES}/${cheque.image}`}
              style={{
                width: "100%",
                objectFit: "cover",
              }}
            />
          </Row>
        </>
      ) : (
        <></>
      )}
    </Column>
  );
};

export default ChequeDetails;
